import React, { useEffect, useMemo, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import { Row, Col, Modal, Button, Form, Alert, Badge, ListGroup, Spinner } from 'react-bootstrap';
import axios from 'axios';
import Card from '../../../components/Card';
import HeaderBread from '../../../components/partials/components/header-breadcrumb';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import bootstrapPlugin from '@fullcalendar/bootstrap';
import itLocale from '@fullcalendar/core/locales/it';
import '@fortawesome/fontawesome-free/css/all.css';
import { useLanguage } from '../../../i18n/LanguageContext';
import { useAuth } from '../../../components/auth/AuthProvider';
import apiUrl from '../../../config/api';

const readStoredJson = (key, fallback = []) => {
  try {
    const storedValue = localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : fallback;
  } catch (error) {
    return fallback;
  }
};

const setStoredValue = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn('Unable to save calendar events:', error);
  }
};

const getDateKey = (dateValue) => {
  const date = typeof dateValue === 'string' ? new Date(`${dateValue}T00:00:00`) : dateValue;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const todayDateKey = () => getDateKey(new Date());

const isPastDateKey = (dateKey) => Boolean(dateKey) && dateKey < todayDateKey();

const Calender = () => {
  const { language, t } = useLanguage();
  const { isAuthLoading, userDetails } = useAuth();
  const text = t.calendarPage;
  const calendarLocale = language === 'it' ? itLocale : 'en';
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const calendarStorageKey = `quizify-calendar-events-${userDetails?.email || 'local'}`;

  const [archives, setArchives] = useState([]);
  const [subjectsByArchiveId, setSubjectsByArchiveId] = useState({});
  const [events, setEvents] = useState([]);
  const [loadedStorageKey, setLoadedStorageKey] = useState('');
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEventListModal, setShowEventListModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [eventType, setEventType] = useState('archive');
  const [selectedArchiveId, setSelectedArchiveId] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [eventValidationError, setEventValidationError] = useState('');

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
  }), [token]);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    const storedEvents = readStoredJson(calendarStorageKey);
    setEvents(Array.isArray(storedEvents) ? storedEvents : []);
    setLoadedStorageKey(calendarStorageKey);
  }, [calendarStorageKey, isAuthLoading]);

  useEffect(() => {
    if (isAuthLoading || loadedStorageKey !== calendarStorageKey) {
      return;
    }

    setStoredValue(calendarStorageKey, JSON.stringify(events));
  }, [calendarStorageKey, events, isAuthLoading, loadedStorageKey]);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (!token) {
      setIsLoadingData(false);
      return;
    }

    setIsLoadingData(true);
    setError('');

    axios.post(`${apiUrl}/getArchives`, {}, { headers: authHeaders })
      .then(async (response) => {
        const nextArchives = response.data || [];
        const subjectEntries = await Promise.all(nextArchives.map(async (archive) => {
          const subjectsResponse = await axios.post(
            `${apiUrl}/getSubjects`,
            { archiveId: archive._id },
            { headers: authHeaders }
          );

          return [archive._id, subjectsResponse.data || []];
        }));

        setArchives(nextArchives);
        setSubjectsByArchiveId(Object.fromEntries(subjectEntries));
        setSelectedArchiveId(nextArchives[0]?._id || '');
      })
      .catch((requestError) => {
        setError(requestError.response?.data?.message || requestError.message);
      })
      .finally(() => {
        setIsLoadingData(false);
      });
  }, [authHeaders, isAuthLoading, token]);

  const subjectsForSelectedArchive = useMemo(
    () => subjectsByArchiveId[selectedArchiveId] || [],
    [selectedArchiveId, subjectsByArchiveId]
  );

  useEffect(() => {
    setSelectedSubjectId(subjectsForSelectedArchive[0]?._id || '');
  }, [selectedArchiveId, subjectsForSelectedArchive]);

  const calendarEvents = useMemo(() => events.map((eventItem) => ({
    id: eventItem.id,
    title: eventItem.title,
    date: eventItem.date,
    allDay: true,
    textColor: eventItem.type === 'archive' ? 'rgba(58,87,232,1)' : 'rgba(8,130,12,1)',
    backgroundColor: eventItem.type === 'archive' ? 'rgba(58,87,232,0.14)' : 'rgba(8,130,12,0.14)',
    borderColor: eventItem.type === 'archive' ? 'rgba(58,87,232,1)' : 'rgba(8,130,12,1)',
    extendedProps: eventItem,
  })), [events]);

  const eventsForSelectedDate = useMemo(() => (
    events.filter((eventItem) => eventItem.date === selectedDate)
  ), [events, selectedDate]);

  const selectedDateIsPast = isPastDateKey(selectedDate);

  const getEventValidationError = ({
    date = selectedDate,
    type = eventType,
    archiveId = selectedArchiveId,
    subjectId = selectedSubjectId,
  } = {}) => {
    if (isPastDateKey(date)) {
      return text.pastDateBlocked;
    }

    const existingEventsForDate = events.filter((eventItem) => eventItem.date === date);
    const activeEvents = events.filter((eventItem) => !isPastDateKey(eventItem.date));
    const activeEventsOnOtherDates = activeEvents.filter((eventItem) => eventItem.date !== date);
    const hasArchiveForSameArchive = existingEventsForDate.some((eventItem) => (
      eventItem.type === 'archive' && eventItem.archiveId === archiveId
    ));
    const hasSubjectForSameArchive = existingEventsForDate.some((eventItem) => (
      eventItem.type === 'subject' && eventItem.archiveId === archiveId
    ));
    const hasSameSubject = existingEventsForDate.some((eventItem) => (
      eventItem.type === 'subject' && eventItem.subjectId === subjectId
    ));
    const hasActiveArchiveOnOtherDate = activeEventsOnOtherDates.some((eventItem) => (
      eventItem.type === 'archive' && eventItem.archiveId === archiveId
    ));
    const hasActiveSubjectOnOtherDate = activeEventsOnOtherDates.some((eventItem) => (
      eventItem.type === 'subject' && eventItem.subjectId === subjectId
    ));
    const hasActiveSubjectFromSameArchiveOnOtherDate = activeEventsOnOtherDates.some((eventItem) => (
      eventItem.type === 'subject' && eventItem.archiveId === archiveId
    ));

    if (type === 'archive' && hasSubjectForSameArchive) {
      return text.archiveBlockedBySubjects;
    }

    if (type === 'archive' && hasArchiveForSameArchive) {
      return text.archiveAlreadyAdded;
    }

    if (type === 'subject' && hasArchiveForSameArchive) {
      return text.subjectBlockedByArchive;
    }

    if (type === 'subject' && hasSameSubject) {
      return text.subjectAlreadyAdded;
    }

    if (type === 'archive' && hasActiveArchiveOnOtherDate) {
      return text.archiveAlreadyScheduled;
    }

    if (type === 'archive' && hasActiveSubjectFromSameArchiveOnOtherDate) {
      return text.archiveBlockedByActiveSubjects;
    }

    if (type === 'subject' && hasActiveArchiveOnOtherDate) {
      return text.subjectBlockedByActiveArchive;
    }

    if (type === 'subject' && hasActiveSubjectOnOtherDate) {
      return text.subjectAlreadyScheduled;
    }

    return '';
  };

  useEffect(() => {
    if (!showEventModal) {
      return;
    }

    setEventValidationError(getEventValidationError());
  }, [showEventModal, selectedDate, eventType, selectedArchiveId, selectedSubjectId, events]);

  const formatSelectedDate = (dateKey) => {
    if (!dateKey) {
      return '';
    }

    return new Intl.DateTimeFormat(language === 'it' ? 'it-IT' : 'en-US', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(new Date(`${dateKey}T00:00:00`));
  };

  const openEventModal = (dateKey) => {
    if (isPastDateKey(dateKey)) {
      setSelectedDate(dateKey);
      setEventValidationError(text.pastDateBlocked);
      setShowEventListModal(true);
      return;
    }

    const nextArchiveId = selectedArchiveId || archives[0]?._id || '';
    const nextSubjects = subjectsByArchiveId[nextArchiveId] || [];

    setSelectedDate(dateKey);
    setEventType('archive');
    setSelectedArchiveId(nextArchiveId);
    setSelectedSubjectId(nextSubjects[0]?._id || '');
    setEventValidationError('');
    setShowEventModal(true);
  };

  const openEventListModal = (dateKey) => {
    setSelectedDate(dateKey);
    setShowEventListModal(true);
  };

  const handleAddEvent = () => {
    const archive = archives.find((archiveItem) => archiveItem._id === selectedArchiveId);
    const subject = subjectsForSelectedArchive.find((subjectItem) => subjectItem._id === selectedSubjectId);
    const validationError = getEventValidationError();

    if (validationError) {
      setEventValidationError(validationError);
      return;
    }

    if (!archive || (eventType === 'subject' && !subject)) {
      return;
    }

    const title = eventType === 'archive'
      ? archive.name
      : `${archive.name} - ${subject.name}`;

    setEvents((previousEvents) => [
      ...previousEvents,
      {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        date: selectedDate,
        type: eventType,
        archiveId: archive._id,
        archiveName: archive.name,
        subjectId: subject?._id || null,
        subjectName: subject?.name || '',
        title,
      },
    ]);
    setShowEventModal(false);
  };

  const handleRemoveEvent = (eventId) => {
    setEvents((previousEvents) => previousEvents.filter((eventItem) => eventItem.id !== eventId));
  };

  return (
    <>
      <HeaderBread />
      <Row>
        <Col lg="12">
          <Card className="quizify-calendar-card" data-aos="fade-up">
            <Card.Body>
              <div
                className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3"
                data-aos="fade-up"
                data-aos-delay="80"
              >
                <div>
                  <h4 className="mb-1">{text.title}</h4>
                  <p className="mb-0 text-muted">{text.subtitle}</p>
                </div>
                <Badge bg="primary" className="px-3 py-2">
                  {text.eventsCount(events.length)}
                </Badge>
              </div>

              {error && <Alert variant="danger">{error}</Alert>}

              {isLoadingData ? (
                <div className="py-5 text-center">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : (
                <>
                  {!archives.length && (
                    <Alert variant="info">{text.noArchives}</Alert>
                  )}
                  <div className="quizify-calendar-wrap" data-aos="fade-up" data-aos-delay="140">
                    <FullCalendar
                      plugins={[dayGridPlugin, listPlugin, bootstrapPlugin]}
                      locale={calendarLocale}
                      buttonText={{
                        today: text.today,
                        month: text.month,
                        week: text.week,
                        day: text.day,
                        list: text.list,
                      }}
                      headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,dayGridWeek,dayGridDay,listWeek',
                      }}
                      fixedWeekCount={false}
                      expandRows={true}
                      dayMaxEventRows={true}
                      moreLinkContent={(args) => text.moreEvents(args.num)}
                      moreLinkClick={(info) => {
                        openEventListModal(getDateKey(info.date));
                        return true;
                      }}
                      eventClick={(info) => {
                        info.jsEvent.preventDefault();
                        openEventListModal(getDateKey(info.event.start));
                      }}
                      dayCellDidMount={(info) => {
                        const dateKey = getDateKey(info.date);
                        const isPastDay = isPastDateKey(dateKey);

                        info.el.classList.add('quizify-calendar-day');
                        if (isPastDay) {
                          info.el.classList.add('quizify-calendar-day-past');
                          info.el.setAttribute('aria-disabled', 'true');
                          info.el.setAttribute('title', text.pastDateBlocked);
                        }
                        info.el.addEventListener('click', (event) => {
                          if (event.target.closest('.fc-event, .fc-more-link')) {
                            return;
                          }

                          if (isPastDay) {
                            if (events.some((eventItem) => eventItem.date === dateKey)) {
                              openEventListModal(dateKey);
                            }
                            return;
                          }

                          openEventModal(dateKey);
                        });
                      }}
                      events={calendarEvents}
                    />
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showEventModal} onHide={() => setShowEventModal(false)} centered className="quizify-calendar-modal">
        <Modal.Header closeButton>
          <Modal.Title>{text.addEventTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3 text-capitalize">{formatSelectedDate(selectedDate)}</p>

          {!archives.length ? (
            <Alert variant="info">{text.noArchives}</Alert>
          ) : (
            <Form>
              {selectedDateIsPast && (
                <Alert variant="warning">{text.pastDateBlocked}</Alert>
              )}
              {eventValidationError && !selectedDateIsPast && (
                <Alert variant="warning">{eventValidationError}</Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label>{text.eventType}</Form.Label>
                <Form.Select
                  value={eventType}
                  disabled={selectedDateIsPast}
                  onChange={(event) => {
                    setEventType(event.target.value);

                    if (event.target.value === 'subject' && !selectedSubjectId) {
                      setSelectedSubjectId(subjectsForSelectedArchive[0]?._id || '');
                    }
                  }}
                >
                  <option value="archive">{text.archive}</option>
                  <option value="subject">{text.subject}</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{text.selectArchive}</Form.Label>
                <Form.Select
                  value={selectedArchiveId}
                  disabled={selectedDateIsPast}
                  onChange={(event) => setSelectedArchiveId(event.target.value)}
                >
                  {archives.map((archive) => (
                    <option key={archive._id} value={archive._id}>{archive.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              {eventType === 'subject' && (
                <Form.Group>
                  <Form.Label>{text.selectSubject}</Form.Label>
                  <Form.Select
                    value={selectedSubjectId}
                    onChange={(event) => setSelectedSubjectId(event.target.value)}
                    disabled={selectedDateIsPast || !subjectsForSelectedArchive.length}
                  >
                    {!subjectsForSelectedArchive.length && (
                      <option value="">{text.noSubjects}</option>
                    )}
                    {subjectsForSelectedArchive.map((subject) => (
                      <option key={subject._id} value={subject._id}>{subject.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="soft-secondary" onClick={() => setShowEventModal(false)}>
            {text.cancel}
          </Button>
          <Button
            variant="primary"
            onClick={handleAddEvent}
            disabled={
              !archives.length
              || selectedDateIsPast
              || Boolean(eventValidationError)
              || (eventType === 'subject' && !selectedSubjectId)
            }
          >
            {text.addEvent}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEventListModal} onHide={() => setShowEventListModal(false)} centered className="quizify-calendar-modal">
        <Modal.Header closeButton>
          <Modal.Title>{text.dayEventsTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3 text-capitalize">{formatSelectedDate(selectedDate)}</p>

          {eventsForSelectedDate.length ? (
            <ListGroup className="quizify-calendar-event-list">
              {eventsForSelectedDate.map((eventItem) => (
                <ListGroup.Item key={eventItem.id}>
                  <div className="quizify-calendar-event-content">
                    <Badge bg={eventItem.type === 'archive' ? 'primary' : 'success'} className="mb-2">
                      {eventItem.type === 'archive' ? text.archive : text.subject}
                    </Badge>
                    <h6 className="quizify-calendar-event-title mb-1">{eventItem.title}</h6>
                    {eventItem.type === 'subject' && (
                      <small className="quizify-calendar-event-meta text-muted d-block">{eventItem.archiveName}</small>
                    )}
                  </div>
                  <Button
                    variant="soft-danger"
                    size="sm"
                    className="quizify-calendar-event-action"
                    onClick={() => handleRemoveEvent(eventItem.id)}
                  >
                    {text.remove}
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <Alert variant="info" className="mb-0">{text.noEventsForDay}</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="soft-primary" onClick={() => {
            setShowEventListModal(false);
            openEventModal(selectedDate);
          }} disabled={selectedDateIsPast}>
            {text.addEvent}
          </Button>
          <Button variant="secondary" onClick={() => setShowEventListModal(false)}>
            {text.close}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Calender;
